import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useListContext, useRecordContext } from "react-admin";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { roleContext, useMessagingReadRole } from "@/hooks/useMessagingReadRole";
import { GET_LEDGER_MESSAGE_STATES } from "@/queries/GetLedgerMessages";
import { EMessageAttemptState, MESSAGE_ATTEMPT_STATES } from "@/types/messaging";
import { electoralLogDelivery } from "../electoralLogChannel";
import { reasonText } from "@/services/messagingReasons";
var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var DAY_MS = 24 * 60 * 60 * 1000;
var LedgerContext = createContext({});
/**
 * Reads the current state of the messages the page's entries record, in one
 * query with messaging-config-read. Without the role the entries show the
 * state they were written with.
 */
export var LedgerStates = function (_a) {
    var children = _a.children;
    var data = useListContext().data;
    var tenantId = useTenantStore()[0];
    var role = useMessagingReadRole();
    var ids = useMemo(function () {
        return Array.from(new Set((data !== null && data !== void 0 ? data : [])
            .map(function (record) { var _a; return (_a = electoralLogDelivery(record.message)) === null || _a === void 0 ? void 0 : _a.messageId; })
            .filter(function (id) { return !!id && UUID.test(id); }))).sort();
    }, [data]);
    var ledger = useQuery(GET_LEDGER_MESSAGE_STATES, {
        variables: { tenantId: tenantId, ids: ids },
        skip: !role || !tenantId || ids.length === 0,
        context: roleContext(role),
    }).data;
    var byId = useMemo(function () {
        var _a;
        return Object.fromEntries(((_a = ledger === null || ledger === void 0 ? void 0 : ledger.sequent_backend_message) !== null && _a !== void 0 ? _a : []).map(function (message) { return [message.id, message]; }));
    }, [ledger]);
    return _jsx(LedgerContext.Provider, { value: byId, children: children });
};
/**
 * The state shown for a message: Unknown for more than 24 hours is
 * Unresolved, never Failed.
 */
export var ledgerStatus = function (state, since, now) {
    if (now === void 0) { now = Date.now(); }
    var known = MESSAGE_ATTEMPT_STATES.find(function (candidate) { return candidate === state; });
    if (known === EMessageAttemptState.UNKNOWN && since) {
        var age = now - new Date(since).getTime();
        if (age > DAY_MS)
            return "UNRESOLVED";
    }
    return known;
};
/** The Status of a Logs row: the message's current state in the ledger. */
export var LedgerStatusField = function () {
    var _a;
    var t = useTranslation().t;
    var record = useRecordContext();
    var ledger = useContext(LedgerContext);
    var delivery = electoralLogDelivery(record === null || record === void 0 ? void 0 : record.message);
    if (!(delivery === null || delivery === void 0 ? void 0 : delivery.outcome) && !(delivery === null || delivery === void 0 ? void 0 : delivery.messageId)) {
        return _jsx("span", { children: "-" });
    }
    var current = delivery.messageId ? ledger[delivery.messageId] : undefined;
    var status = current
        ? ledgerStatus(current.state, current.created_at)
        : ledgerStatus(delivery.outcome, undefined);
    if (!status) {
        return _jsx("span", { children: (_a = delivery.outcome) !== null && _a !== void 0 ? _a : "-" });
    }
    // The reason of the state shown: the ledger's current one, else as logged.
    var reason = reasonText(t, current ? current.error : delivery.reason);
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", component: "span", title: current
                    ? t("messaging.stateHelp.".concat(status))
                    : t("messaging.logs.statusAsLogged"), sx: { whiteSpace: "nowrap" }, children: t("messaging.state.".concat(status)) }), reason ? (_jsx(Typography, { variant: "caption", component: "div", color: "text.secondary", children: reason })) : null] }));
};
